import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Package, Plus, Trash2, Pencil, Check, X, Loader2, AlertTriangle, CheckCircle2, Ban, Glasses } from "lucide-react";
import { api } from "@/lib/api";
import { fmtDate, fmtEUR } from "@/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const LEVELS = {
  green: { label: "STOCK OK", cls: "border-emerald-300 bg-emerald-50 text-emerald-700", Icon: CheckCircle2 },
  orange: { label: "À COMMANDER PROCHAINEMENT", cls: "border-amber-300 bg-amber-50 text-amber-800", Icon: AlertTriangle },
  red: { label: "RUPTURE PRÉVISIONNELLE", cls: "border-rose-300 bg-rose-50 text-rose-700", Icon: Ban },
};

const ACC_LEVELS = {
  green: { label: "Stock OK", cls: "border-emerald-300 bg-emerald-50 text-emerald-700", Icon: CheckCircle2 },
  orange: { label: "À recommander", cls: "border-amber-300 bg-amber-50 text-amber-800", Icon: AlertTriangle },
  red: { label: "Stock faible", cls: "border-rose-300 bg-rose-50 text-rose-700", Icon: Ban },
};

const PRODUCT_LABEL = { ds620: "DS620 (400 photos)", rx1hs: "RX1HS (700 photos)", reste_ds620: "Reste DS620" };

const MOTIFS = [
  "Erreur de saisie", "Inventaire / correction", "Consommable défectueux", "Consommable cassé",
  "Perte", "Vol", "Consommable inutilisable", "Autre",
];

const fmtUnits = (u) => String(Math.floor(u)); // affichage : partie entière inférieure ; la précision reste en interne

function Badge({ level, map, testid }) {
  const L = map[level] || map.green;
  return (
    <span data-testid={testid} className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-extrabold ${L.cls}`}>
      <L.Icon className="h-3.5 w-3.5" /> {L.label}
    </span>
  );
}

export default function Stock() {
  const [st, setSt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mvt, setMvt] = useState({ product: "ds620", qty: "", motive: "Inventaire / correction", note: "" });
  const [sending, setSending] = useState(false);
  const [newReste, setNewReste] = useState("");
  const [editReste, setEditReste] = useState(null);
  const [delay, setDelay] = useState(7);
  const [safety, setSafety] = useState(2);
  const [newAcc, setNewAcc] = useState({ name: "", qty: "", seuil: "", per_event: "" });
  const [editAcc, setEditAcc] = useState(null);
  const [invOpen, setInvOpen] = useState(false);
  const [invVals, setInvVals] = useState({ ds620: "", rx1hs: "" });

  const load = async () => {
    try {
      const r = await api.get("/stock");
      setSt(r.data);
      setDelay(r.data.reorder_delay_days);
      setSafety(r.data.stock_safety_days);
    } catch {
      toast.error("Chargement du stock impossible");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const move = async (mode) => {
    const q = parseInt(mvt.qty, 10);
    if (!q || q <= 0) { toast.error("Quantité positive requise"); return; }
    if (mvt.motive === "Autre" && !mvt.note.trim() && mode !== "reception") { toast.error("Précisez le motif dans la note"); return; }
    setSending(true);
    try {
      const payload = { product: mvt.product, qty: mode === "retirer" ? -q : q, motive: mode === "reception" ? "Réception commande" : mvt.motive, note: mvt.note };
      const r = await api.post(mode === "reception" ? "/stock/reception" : "/stock/adjust", payload);
      setSt(r.data);
      setMvt({ ...mvt, qty: "", note: "" });
      toast.success("Mouvement enregistré — stock recalculé");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setSending(false);
    }
  };

  const quickAdjust = async (accId, delta) => {
    try {
      const r = await api.post("/stock/adjust", { product: `acc:${accId}`, qty: delta, motive: "Ajustement manuel" });
      setSt(r.data);
      toast.success(`Stock ajusté : ${delta > 0 ? "+1" : "−1"}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const addReste = async () => {
    const photos = parseInt(newReste, 10);
    if (!photos || photos <= 0) { toast.error("Nombre de photos requis"); return; }
    try {
      const r = await api.post("/stock/restes", { photos });
      setSt(r.data);
      setNewReste("");
      toast.success("Reste ajouté");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const updateReste = async () => {
    try {
      const r = await api.put(`/stock/restes/${editReste.id}`, { photos: parseInt(editReste.photos, 10) || 0 });
      setSt(r.data);
      setEditReste(null);
      toast.success("Reste mis à jour — correction tracée dans l'historique");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const deleteReste = async (id) => {
    const r = await api.delete(`/stock/restes/${id}`);
    setSt(r.data);
    toast.success("Reste supprimé");
  };

  const addAcc = async () => {
    if (!newAcc.name.trim()) { toast.error("Nom de l'accessoire requis"); return; }
    try {
      const r = await api.post("/stock/accessories", {
        name: newAcc.name.trim(), qty: parseInt(newAcc.qty, 10) || 0, seuil: parseInt(newAcc.seuil, 10) || 0,
        gestion: "consommable", per_event: parseInt(newAcc.per_event, 10) || 0,
      });
      setSt(r.data);
      setNewAcc({ name: "", qty: "", seuil: "", per_event: "" });
      toast.success("Accessoire ajouté");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const saveAcc = async () => {
    try {
      const r = await api.put(`/stock/accessories/${editAcc.id}`, {
        name: editAcc.name, seuil: parseInt(editAcc.seuil, 10) || 0, gestion: "consommable",
        per_event: parseInt(editAcc.per_event, 10) || 0,
      });
      setSt(r.data);
      setEditAcc(null);
      toast.success("Accessoire mis à jour");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const doInventory = async () => {
    const payload = {};
    if (invVals.ds620 !== "") payload.ds620 = parseInt(invVals.ds620, 10) || 0;
    if (invVals.rx1hs !== "") payload.rx1hs = parseInt(invVals.rx1hs, 10) || 0;
    if (!Object.keys(payload).length) { toast.error("Saisissez au moins une valeur constatée"); return; }
    try {
      const r = await api.post("/stock/inventory", payload);
      setSt(r.data);
      setInvOpen(false);
      setInvVals({ ds620: "", rx1hs: "" });
      toast.success("Inventaire enregistré — ajustement tracé dans l'historique");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const deleteAcc = async (id) => {
    const r = await api.delete(`/stock/accessories/${id}`);
    setSt(r.data);
    toast.success("Accessoire supprimé");
  };

  const saveParams = async () => {
    try {
      const cur = await api.get("/settings");
      await api.put("/settings", { ...cur.data, reorder_delay_days: parseInt(delay, 10) || 0, stock_safety_days: parseInt(safety, 10) || 0 });
      toast.success("Paramètres enregistrés");
      load();
    } catch {
      toast.error("Enregistrement impossible");
    }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  if (!st) return null;

  const global = LEVELS[st.level] || LEVELS.green;

  return (
    <div className="space-y-5" data-testid="stock-page">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-xl bg-slate-900 flex items-center justify-center shrink-0">
          <Package className="h-5 w-5 text-white" />
        </div>
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Stock</h1>
          <p className="text-xs text-slate-500">Consommables photo et accessoires — recalcul automatique à chaque réservation, départ en livraison ou mouvement.</p>
        </div>
      </div>

      {st.level !== "green" && (
        <div data-testid="stock-alert" className={`rounded-2xl border-2 p-4 space-y-1 ${global.cls}`}>
          <p className="font-heading font-extrabold flex items-center gap-2">
            <global.Icon className="h-5 w-5" />
            {st.level === "red" ? "⚠️ COMMANDE CONSOMMABLE À PRÉVOIR" : "Stock bientôt insuffisant"}
          </p>
          {st.rupture && (
            <p className="text-sm">
              {PRODUCT_LABEL[st.rupture.product]} — {st.rupture.reason}. Réservation concernée : <strong>{st.rupture.client}</strong> (formule {st.rupture.formula}).
              <br />Rupture prévisionnelle : avant le <strong>{fmtDate(st.rupture.date)}</strong>.
              {st.order_by && <> Commande recommandée avant le : <strong>{fmtDate(st.order_by)}</strong>.</>}
            </p>
          )}
          {st.level === "orange" && !st.rupture && (
            <p className="text-sm">Le stock couvre les réservations immédiates mais approche du seuil minimum.</p>
          )}
        </div>
      )}

      <section className="space-y-3">
        <h2 className="font-heading text-lg font-bold">Consommables photo</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <section data-testid="stock-card-ds620" className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 space-y-2">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <h3 className="font-heading font-extrabold">DNP DS620</h3>
              <Badge level={st.ds620.level} map={LEVELS} testid="stock-level-ds620" />
            </div>
            <p className="text-sm"><span data-testid="stock-ds620-units" className="font-heading text-2xl font-extrabold font-mono tabular-nums">{fmtUnits(st.ds620.units)}</span> <span className="text-slate-600">consommables 400 photos (<span data-testid="stock-ds620-photos">{st.ds620.photos_total}</span> photos)</span></p>
            <p className="text-sm text-slate-600">Restes utilisables (≥ 100 photos) : <strong data-testid="stock-ds620-restes-usables">{st.ds620.restes_usables}</strong> · Photos dans les restes : <strong data-testid="stock-ds620-restes-photos">{st.ds620.restes_total_photos}</strong></p>
            <p className="text-sm text-slate-600">Réservé pour prestations futures : <strong data-testid="stock-ds620-reserved" className="font-mono tabular-nums">{st.ds620.reserved_photos} photos</strong></p>
            <p className="text-sm text-slate-600">Disponible prévisionnel : <strong data-testid="stock-ds620-forecast" className="font-mono tabular-nums">{st.ds620.forecast_photos} photos</strong> (≈ {fmtUnits(Math.floor(st.ds620.forecast_photos) / 400)} consommables)</p>
          </section>

          <section data-testid="stock-card-rx1hs" className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 space-y-2">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <h3 className="font-heading font-extrabold">DNP RX1HS</h3>
              <Badge level={st.rx1hs.level} map={LEVELS} testid="stock-level-rx1hs" />
            </div>
            <p className="text-sm"><span data-testid="stock-rx1hs-units" className="font-heading text-2xl font-extrabold font-mono tabular-nums">{st.rx1hs.units}</span> <span className="text-slate-600">consommable{st.rx1hs.units > 1 ? "s" : ""} 700 photos</span></p>
            <p className="text-sm text-slate-600">Réservé (formules 700 futures) : <strong data-testid="stock-rx1hs-reserved" className="font-mono tabular-nums">{st.rx1hs.reserved}</strong></p>
            <p className="text-sm text-slate-600">Disponible prévisionnel : <strong data-testid="stock-rx1hs-forecast" className="font-mono tabular-nums">{st.rx1hs.forecast}</strong> consommable{st.rx1hs.forecast > 1 ? "s" : ""}</p>
          </section>
        </div>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5" data-testid="stock-value">
        <h2 className="font-heading font-bold mb-2">Valeur du stock (tarif fournisseur sans remise)</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
          <p className="text-slate-600">DS620 (54 € / 400 photos, restes au prorata) : <strong className="font-mono tabular-nums" data-testid="stock-value-ds620">{fmtEUR(st.value.ds620)}</strong></p>
          <p className="text-slate-600">RX1HS (80 € / 700 photos) : <strong className="font-mono tabular-nums" data-testid="stock-value-rx1hs">{fmtEUR(st.value.rx1hs)}</strong></p>
          <p className="font-semibold">Valeur totale : <strong className="font-mono tabular-nums text-emerald-700" data-testid="stock-value-total">{fmtEUR(st.value.total)}</strong></p>
        </div>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 space-y-3" data-testid="stock-inventory">
        {!invOpen ? (
          <Button variant="outline" data-testid="stock-inventory-open" onClick={() => setInvOpen(true)}>
            Faire un inventaire / saisir le stock réel
          </Button>
        ) : (
          <div className="space-y-3">
            <p className="text-sm font-semibold text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              ⚠️ Saisissez les quantités réellement constatées : le stock physique sera remplacé par ces valeurs (ajustement tracé dans l'historique).
            </p>
            <div className="grid grid-cols-2 gap-3 max-w-md">
              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-600">DS620 — stock constaté</label>
                <Input data-testid="stock-inventory-ds620" type="number" min="0" placeholder={fmtUnits(st.ds620.units)} value={invVals.ds620} onChange={(e) => setInvVals({ ...invVals, ds620: e.target.value })} />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-600">RX1HS — stock constaté</label>
                <Input data-testid="stock-inventory-rx1hs" type="number" min="0" placeholder={String(st.rx1hs.units)} value={invVals.rx1hs} onChange={(e) => setInvVals({ ...invVals, rx1hs: e.target.value })} />
              </div>
            </div>
            <div className="flex gap-2">
              <Button data-testid="stock-inventory-submit" onClick={doInventory} className="bg-slate-900 hover:bg-slate-800 text-white font-semibold">Valider l'inventaire</Button>
              <Button variant="ghost" data-testid="stock-inventory-cancel" onClick={() => setInvOpen(false)}>Annuler</Button>
            </div>
          </div>
        )}
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 space-y-3" data-testid="stock-movement">
        <div>
          <h2 className="font-heading font-bold">Mouvement de stock</h2>
          <p className="text-xs text-slate-500">Réception, retrait (casse, perte, vol…) ou correction d'inventaire — consommables et accessoires.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <select data-testid="stock-movement-product" value={mvt.product} onChange={(e) => setMvt({ ...mvt, product: e.target.value })} className="h-10 w-full rounded-md border border-input bg-white px-3 text-sm">
            <optgroup label="Consommables photo">
              <option value="ds620">DNP DS620 — 400 photos</option>
              <option value="rx1hs">DNP RX1HS — 700 photos</option>
            </optgroup>
            {st.accessories.length > 0 && (
              <optgroup label="Accessoires photobooth">
                {st.accessories.map((a) => <option key={a.id} value={`acc:${a.id}`}>{a.name}</option>)}
              </optgroup>
            )}
          </select>
          <Input data-testid="stock-movement-qty" type="number" min="1" placeholder="Quantité" value={mvt.qty} onChange={(e) => setMvt({ ...mvt, qty: e.target.value })} />
          <select data-testid="stock-movement-motive" value={mvt.motive} onChange={(e) => setMvt({ ...mvt, motive: e.target.value })} className="h-10 w-full rounded-md border border-input bg-white px-3 text-sm">
            {MOTIFS.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
          <Input data-testid="stock-movement-note" placeholder={mvt.motive === "Autre" ? "Précisez le motif…" : "Note (optionnel)"} value={mvt.note} onChange={(e) => setMvt({ ...mvt, note: e.target.value })} />
        </div>
        <div className="flex flex-wrap gap-2">
          <Button data-testid="stock-reception-submit" onClick={() => move("reception")} disabled={sending} className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold">
            {sending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-1" />} Ajouter une réception
          </Button>
          <Button data-testid="stock-remove-submit" onClick={() => move("retirer")} disabled={sending} variant="outline" className="border-rose-300 text-rose-700 hover:bg-rose-50">
            Retirer du stock
          </Button>
          <Button data-testid="stock-adjust-submit" variant="outline" onClick={() => move("ajuster")} disabled={sending}>
            Ajuster le stock (inventaire)
          </Button>
        </div>
        <p className="text-xs text-slate-500">Si l'ajustement met en danger une réservation future, l'alerte de rupture s'affiche immédiatement.</p>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 space-y-3" data-testid="stock-restes">
        <div>
          <h2 className="font-heading font-bold">Restes DS620 (formules 100 photos)</h2>
          <p className="text-xs text-slate-500">Une formule 100 photos utilise en priorité un reste d'au moins 100 photos — coût consommable 0 €. Au départ en livraison, 100 photos sont déduites du reste affecté.</p>
        </div>
        <div className="flex gap-2">
          <Input data-testid="stock-reste-add-input" type="number" min="1" placeholder="Photos restantes (ex. 175)" value={newReste} onChange={(e) => setNewReste(e.target.value)} className="max-w-[220px]" />
          <Button data-testid="stock-reste-add-submit" onClick={addReste} variant="outline"><Plus className="h-4 w-4 mr-1" /> Ajouter un reste</Button>
        </div>
        {st.ds620.restes.length === 0 && <p className="text-sm text-slate-400" data-testid="stock-restes-empty">Aucun reste enregistré.</p>}
        <ul className="divide-y divide-slate-100">
          {st.ds620.restes.map((r, i) => (
            <li key={r.id} data-testid={`stock-reste-item-${i}`} className="py-2 flex items-center gap-2 text-sm flex-wrap">
              {editReste?.id === r.id ? (
                <>
                  <Input data-testid="stock-reste-edit-input" type="number" min="0" value={editReste.photos} onChange={(e) => setEditReste({ ...editReste, photos: e.target.value })} className="w-28" />
                  <span className="text-slate-500">photos</span>
                  <Button size="icon" variant="ghost" data-testid="stock-reste-edit-save" onClick={updateReste}><Check className="h-4 w-4 text-emerald-600" /></Button>
                  <Button size="icon" variant="ghost" data-testid="stock-reste-edit-cancel" onClick={() => setEditReste(null)}><X className="h-4 w-4" /></Button>
                </>
              ) : (
                <>
                  <span className="font-mono tabular-nums font-semibold">{r.photos} photos</span>
                  {r.photos_initiales !== undefined && r.photos_initiales !== r.photos && (
                    <span className="text-xs text-slate-400">({r.photos_initiales} saisies — {r.photos_initiales - r.photos} réservées pour une formule 100)</span>
                  )}
                  <span className={`text-xs rounded-full px-2 py-0.5 font-semibold ${r.photos >= 100 ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                    {r.photos >= 100 ? "Utilisable (formule 100)" : "Insuffisant pour une formule 100"}
                  </span>
                  <span className="ml-auto flex gap-1">
                    <Button size="icon" variant="ghost" data-testid={`stock-reste-edit-${i}`} onClick={() => setEditReste({ id: r.id, photos: String(r.photos) })}><Pencil className="h-4 w-4 text-slate-500" /></Button>
                    <Button size="icon" variant="ghost" data-testid={`stock-reste-delete-${i}`} onClick={() => deleteReste(r.id)}><Trash2 className="h-4 w-4 text-rose-600" /></Button>
                  </span>
                </>
              )}
            </li>
          ))}
        </ul>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 space-y-3" data-testid="stock-accessories">
        <div>
          <h2 className="font-heading font-bold flex items-center gap-2"><Glasses className="h-5 w-5 text-slate-500" /> Accessoires photobooth</h2>
          <p className="text-xs text-slate-500">
            Suivi des quantités réelles avec seuil d'alerte. Chaque accessoire suivi est <strong>sortant du stock</strong> :
            la « quantité par prestation » est déduite automatiquement à chaque départ en livraison.
          </p>
        </div>
        {st.accessories.length === 0 && <p className="text-sm text-slate-400" data-testid="stock-accessories-empty">Aucun accessoire suivi pour le moment.</p>}
        <ul className="divide-y divide-slate-100">
          {st.accessories.map((a, i) => (
            <li key={a.id} data-testid={`stock-acc-item-${i}`} className="py-2.5 space-y-1.5">
              {editAcc?.id === a.id ? (
                <div className="grid grid-cols-2 sm:grid-cols-[1fr_90px_110px_auto] gap-2 items-center">
                  <Input data-testid="stock-acc-edit-name" value={editAcc.name} onChange={(e) => setEditAcc({ ...editAcc, name: e.target.value })} />
                  <Input data-testid="stock-acc-edit-seuil" type="number" min="0" placeholder="Seuil" value={editAcc.seuil} onChange={(e) => setEditAcc({ ...editAcc, seuil: e.target.value })} />
                  <Input data-testid="stock-acc-edit-per-event" type="number" min="0" placeholder="Qté/prestation" value={editAcc.per_event} onChange={(e) => setEditAcc({ ...editAcc, per_event: e.target.value })} />
                  <span className="flex gap-1">
                    <Button size="icon" variant="ghost" data-testid="stock-acc-edit-save" onClick={saveAcc}><Check className="h-4 w-4 text-emerald-600" /></Button>
                    <Button size="icon" variant="ghost" data-testid="stock-acc-edit-cancel" onClick={() => setEditAcc(null)}><X className="h-4 w-4" /></Button>
                  </span>
                </div>
              ) : (
                <div className="flex items-center gap-2 flex-wrap text-sm">
                  <span className="font-semibold text-slate-800">{a.name}</span>
                  <span className="flex items-center gap-1.5" data-testid={`stock-acc-quick-${i}`}>
                    <button
                      type="button"
                      data-testid={`stock-acc-minus-${i}`}
                      onClick={() => quickAdjust(a.id, -1)}
                      className="h-11 w-11 rounded-xl border-2 border-slate-300 bg-white text-xl font-extrabold text-slate-700 active:bg-rose-50 active:border-rose-400 transition-colors touch-manipulation"
                    >
                      −
                    </button>
                    <span className="font-mono tabular-nums font-bold text-lg min-w-[2.5rem] text-center" data-testid={`stock-acc-qty-${i}`}>{a.qty}</span>
                    <button
                      type="button"
                      data-testid={`stock-acc-plus-${i}`}
                      onClick={() => quickAdjust(a.id, 1)}
                      className="h-11 w-11 rounded-xl border-2 border-slate-300 bg-white text-xl font-extrabold text-slate-700 active:bg-emerald-50 active:border-emerald-400 transition-colors touch-manipulation"
                    >
                      +
                    </button>
                  </span>
                  <span className="text-xs text-slate-500">/ seuil {a.seuil}</span>
                  <Badge level={a.level} map={ACC_LEVELS} testid={`stock-acc-level-${i}`} />
                  <span className="text-xs text-slate-400">Sortant — {a.per_event}/prestation</span>
                  <span className="ml-auto flex gap-1">
                    <Button size="icon" variant="ghost" data-testid={`stock-acc-edit-${i}`} onClick={() => setEditAcc({ id: a.id, name: a.name, seuil: String(a.seuil), gestion: a.gestion, per_event: String(a.per_event) })}><Pencil className="h-4 w-4 text-slate-500" /></Button>
                    <Button size="icon" variant="ghost" data-testid={`stock-acc-delete-${i}`} onClick={() => deleteAcc(a.id)}><Trash2 className="h-4 w-4 text-rose-600" /></Button>
                  </span>
                </div>
              )}
            </li>
          ))}
        </ul>
        <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 space-y-2">
          <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Ajouter un type d'accessoire</p>
          <div className="grid grid-cols-2 sm:grid-cols-[1fr_90px_90px_110px_auto] gap-2">
            <Input data-testid="stock-acc-add-name" placeholder="Nom (ex. Lunettes fantaisie)" value={newAcc.name} onChange={(e) => setNewAcc({ ...newAcc, name: e.target.value })} />
            <Input data-testid="stock-acc-add-qty" type="number" min="0" placeholder="Quantité" value={newAcc.qty} onChange={(e) => setNewAcc({ ...newAcc, qty: e.target.value })} />
            <Input data-testid="stock-acc-add-seuil" type="number" min="0" placeholder="Seuil" value={newAcc.seuil} onChange={(e) => setNewAcc({ ...newAcc, seuil: e.target.value })} />
            <Input data-testid="stock-acc-add-per-event" type="number" min="0" placeholder="Qté/prestation" value={newAcc.per_event} onChange={(e) => setNewAcc({ ...newAcc, per_event: e.target.value })} />
            <Button data-testid="stock-acc-add-submit" onClick={addAcc} variant="outline"><Plus className="h-4 w-4 mr-1" /> Ajouter</Button>
          </div>
        </div>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 space-y-3" data-testid="stock-params">
        <h2 className="font-heading font-bold">Réapprovisionnement consommables</h2>
        <div className="grid grid-cols-2 gap-3 max-w-md">
          <div className="space-y-1">
            <label className="text-xs font-semibold text-slate-600">Délai de réappro (jours)</label>
            <Input data-testid="stock-param-delay" type="number" min="0" value={delay} onChange={(e) => setDelay(e.target.value)} />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-semibold text-slate-600">Marge de sécurité (jours)</label>
            <Input data-testid="stock-param-safety" type="number" min="0" value={safety} onChange={(e) => setSafety(e.target.value)} />
          </div>
        </div>
        <Button data-testid="stock-param-save" onClick={saveParams} className="bg-slate-900 hover:bg-slate-800 text-white font-semibold">Enregistrer</Button>
      </section>

      {st.reservations.length > 0 && (
        <section className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5" data-testid="stock-forecast-list">
          <h2 className="font-heading font-bold mb-2">Besoins des prestations futures</h2>
          <ul className="divide-y divide-slate-100">
            {st.reservations.map((r, i) => (
              <li key={r.id} data-testid={`stock-forecast-${i}`} className="py-2 flex items-center gap-2 text-sm">
                <span className={`h-2 w-2 rounded-full shrink-0 ${r.covered ? "bg-emerald-500" : "bg-rose-500"}`} />
                <span className="font-medium">{fmtDate(r.date)}</span>
                <span className="text-slate-500">{r.client} — formule {r.formula}</span>
                <span className={`ml-auto text-xs font-semibold ${r.covered ? "text-emerald-700" : "text-rose-700"}`}>{r.covered ? "Couvert" : "Non couvert"}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

    </div>
  );
}
